import { Box, Link, Typography, useTheme } from '@mui/material';
import React, { useEffect } from 'react';
import { useMatch } from 'react-router-dom';

import { LegalDocumentTypeDto } from '@/api/generated';
import { useCurrentLegalDocument } from '@/components/layout/footer/util/useCurrentLegalDocument.util.ts';
import { APP_FOOTER_HEIGHT, APP_FOOTER_HEIGHT_VAR } from '@/config/layout.constants.ts';
import { ForceDarkThemeProvider } from '@/config/theme/ThemeMode.context.tsx';
import { RouteEnum } from '@/models/route/Route.model.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix';

const FooterBar: React.FC<{ downloadUrl: string; hasOwnBackground: boolean }> = ({
  downloadUrl,
  hasOwnBackground,
}) => {
  const theme = useTheme();
  const { t } = useTranslationWithPrefix('components.footer');

  return (
    <Box
      component="footer"
      sx={{
        alignItems: 'center',
        backgroundColor: hasOwnBackground ? theme.colors.bgTopbar : undefined,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        justifyContent: 'center',
        minHeight: `${APP_FOOTER_HEIGHT}px`,
        px: 3,
        py: 1.5,
      }}
    >
      <Typography sx={{ color: theme.colors.textSecondary, fontSize: 12 }}>
        {t('termsText')}
      </Typography>
      <Link
        href={downloadUrl}
        rel="noopener noreferrer"
        sx={{ color: theme.colors.textSecondary, fontSize: 12, textUnderlineOffset: 2 }}
        target="_blank"
      >
        {t('termsLink')}
      </Link>
    </Box>
  );
};

export const AppFooter: React.FC = () => {
  const { document: legalDocument } = useCurrentLegalDocument(LegalDocumentTypeDto.TermsOfUse);
  const downloadUrl = legalDocument?.available ? legalDocument.downloadUrl : undefined;
  const isAlwaysDarkView = Boolean(useMatch(RouteEnum.ROOT));

  useEffect(() => {
    if (!downloadUrl) {
      return;
    }

    document.documentElement.style.setProperty(APP_FOOTER_HEIGHT_VAR, `${APP_FOOTER_HEIGHT}px`);

    return () => {
      document.documentElement.style.removeProperty(APP_FOOTER_HEIGHT_VAR);
    };
  }, [downloadUrl]);

  if (!downloadUrl) {
    return null;
  }

  if (isAlwaysDarkView) {
    return (
      <ForceDarkThemeProvider>
        <FooterBar downloadUrl={downloadUrl} hasOwnBackground />
      </ForceDarkThemeProvider>
    );
  }

  return <FooterBar downloadUrl={downloadUrl} hasOwnBackground={false} />;
};
